import React, { useEffect, useState } from "react";
import labIcon from "../../assets/images/LabIcon.png";
import proteinIcon from "../../assets/images/proteinIcon.png";
import totalOrderIcon from "../../assets/images/totalOrderIcon.png";
import OrderDeliveryIcon from "../../assets/images/OrderDeliverIcon.png";
import labTestIcon from "../../assets/images/LabTestIcon.png";
import RevenueIcon from "../../assets/images/RevenueIcon.png";
import { InwardStockTable } from "./Admin/InwardStockTable";
import { useAuth } from "../../context/AuthContext";
import { AdminOrderService } from "../../services/api/AdminOrderService/admin.order.service";

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  changePercentage: number;
  changeText: string;
  onClick?: () => void;
}

interface OrderItem {
  id: string;
  productName: string;
  quantity: string;
  price: string;
  date: string;
}

interface DashboardData {
  totalOrders: {
    count: number;
    change: number;
    trend: string;
  };
  totalOrdersDelivered: {
    count: number;
    change: number;
    trend: string;
  };
  labTestsConducted: {
    count: number;
    change: number;
    trend: string;
  };
  revenue: {
    amount: number;
    change: number;
    trend: string;
  };
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  changePercentage,
  changeText,
  onClick,
}) => {
  const isPositive = changePercentage >= 0;

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm" onClick={onClick}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-gray-500 text-sm">{title}</p>
          <h3 className="text-2xl font-semibold mt-1">{value}</h3>
        </div>
        <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
          {icon}
        </div>
      </div>
      <div className="mt-3">
        <span
          className={`text-sm ${isPositive ? "text-green-500" : "text-red-500"} flex items-center`}
        >
          {isPositive ? "↑" : "↓"} {Math.abs(changePercentage)}% {changeText}
        </span>
      </div>
    </div>
  );
};

const OrderCard: React.FC<{ item: OrderItem }> = ({ item }) => {
  return (
    <div className="flex items-center p-4 border-b border-gray-100">
      <div className="w-10 h-10 rounded bg-purple-100 flex items-center justify-center mr-3">
        <img src={proteinIcon} alt="Product" className="w-6 h-6" />
      </div>
      <div className="flex-1">
        <h4 className="font-medium text-sm">{item.details}</h4>
        <p className="text-gray-500 text-xs">{item.id}</p>
      </div>
      <div className="text-sm text-gray-600">{item.productOrderItems?.length}</div>
      <div className="ml-4 text-sm font-medium">{item.orderTotal}</div>
      <div className="ml-6 text-xs text-gray-500">{item.orderDate}</div>
    </div>
  );
};

export const StockDashboardView: React.FC = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [medicineOrders, setMedicineOrders] = useState([]);
  const [labOrders, setLabOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await AdminOrderService.getDashboardOverview();

        
        console.log("dashboard data", response);

        if (response.success === "invalid") {
          // Handle session expiration if needed
          setError("Session expired. Please log in again.");
          return;
        }
        if (response.success) {
          setDashboardData(response.data);
        } else {
          setError(response.message || "Failed to fetch dashboard data");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await AdminOrderService.getAdminOrdersListing(currentPage, pageSize, {
          hasPrescription: true,
        });

        console.log("Raw API Response:", response);

        if (!isMounted) return;

        const orders = response?.data?.data || [];
        const totalItemsCount = response?.data?.pagination?.totalCount || 0;
        const totalPagesCount = response?.data?.pagination?.totalPages || 1;

        setTotalCount(totalItemsCount);
        setTotalPages(totalPagesCount);

        const transformedOrders = orders.map((order: any) => {
          const labStatus = order.orderStatus === "PENDING_ADMIN" ? "new" : "";

          const patientName =
            order.patientName ||
            order.patient?.name ||
            `Patient ${order.patientId?.slice(0, 6) || "XXXXXX"}`;

          return {
            ...order,
            id: order.id,
            patientName,
            orderNumber: order.orderNumber || `ORD-${order.id}`,
            orderType: order.orderType || "UNKNOWN",
            orderDate: new Date(order.orderDate).toLocaleDateString(),
            details: (order.productOrderItems || [])
              .map((item: any) => item.productName)
              .join(", "),
            labStatus,
            hasPrescription: order.hasPrescription === true,
            paymentStatus: order.paymentStatus || "Pending",
            status: (order.paymentStatus || "Pending").toLowerCase(),
          };
        });

        const medOrders = transformedOrders.filter((order) =>
          order.orderType?.toUpperCase().includes("PRESCRIPTION")
        );

        const labOrders = transformedOrders.filter((order) =>
          order.orderType?.toUpperCase().includes("LAB_TEST")
        );

        console.log("med", medOrders, "lab", labOrders);

        if (isMounted) {
          setMedicineOrders(medOrders);
          setLabOrders(labOrders);
        }
      } catch (err) {
        console.error("Fetch Error:", err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchOrders();

    return () => {
      isMounted = false;
    };
  }, [currentPage]);

  return (
    <div className="bg-gray-50 w-full">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Total Orders"
          value={dashboardData?.totalOrders.count || 0}
          icon={<img src={totalOrderIcon} alt="not-found" className="w-10 h-10" />}
          changePercentage={dashboardData?.totalOrders.change || 0}
          changeText="from last month"
        />

        <StatCard
          title="Total Orders Delivered"
          value={dashboardData?.totalOrdersDelivered.count || 0}
          icon={<img src={OrderDeliveryIcon} alt="not-found" className="w-10 h-10" />}
          changePercentage={dashboardData?.totalOrdersDelivered.change || 0}
          changeText="from last month"
        />

        <StatCard
          title="Overall Lab Tests Conducted"
          value={dashboardData?.labTestsConducted.count || 0}
          icon={<img src={labTestIcon} alt="not-found" className="w-10 h-10" />}
          changePercentage={dashboardData?.labTestsConducted.change || 0}
          changeText="from last month"
        />

        <StatCard
          title="Revenue YTD"
          value={`₹ ${dashboardData?.revenue.amount.toLocaleString("en-IN") || 0}`}
          icon={<img src={RevenueIcon} alt="not-found" className="w-10 h-10" />}
          changePercentage={dashboardData?.revenue.change || 0}
          changeText="increased from last month"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-4">New Orders - {medicineOrders.length}</h3>
          <div
            className="h-[280px] overflow-y-scroll"
            style={{
              scrollbarWidth: "thin",
              scrollBehavior: "smooth",
            }}
          >
            <div>
              {medicineOrders.map((order, index) => (
                <OrderCard key={index} item={order} />
              ))}
            </div>
          </div>
        </div>

        {/* <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-4">New Lab Tests {}</h3>
          <div>
            {tests.map((test, index) => (
              <TestCard key={index} item={test} />
            ))}
          </div>
        </div> */}
      </div>

      {user?.role.toLocaleLowerCase() === "clinic" && <InwardStockTable />}
    </div>
  );
};
